import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as querySubStatusesApi from './query-sub-statuses.api';

import type {
  CreateQuerySubStatusRequest,
  QuerySubStatusListQuery,
  UpdateQuerySubStatusRequest,
} from '@utazzo/shared';

const QK = {
  list: (query: QuerySubStatusListQuery) => ['query-sub-statuses', 'list', query] as const,
  detail: (id: string) => ['query-sub-statuses', 'detail', id] as const,
  statusOptions: ['query-statuses', 'options'] as const,
};

export const useQuerySubStatusesList = (query: QuerySubStatusListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => querySubStatusesApi.listQuerySubStatuses(query),
    staleTime: 30_000,
  });

/**
 * Slim query status options for the parent picker. Keyed under
 * `['query-statuses', 'options']` so any query-statuses feature can share the
 * same cache entry.
 */
export const useQueryStatusOptions = () =>
  useQuery({
    queryKey: QK.statusOptions,
    queryFn: querySubStatusesApi.listQueryStatusOptions,
    staleTime: 5 * 60_000,
  });

/**
 * Cross-cache invalidation for sub-status mutations. Leads embed
 * `currentSubStatus` (and stage history embeds `from/toSubStatus`) - when
 * the master changes, those embedded copies on cached lead rows go stale.
 * See ADR-0032 §18.
 */
const invalidateSubStatusFanout = (qc: ReturnType<typeof useQueryClient>): void => {
  qc.invalidateQueries({ queryKey: ['query-sub-statuses', 'list'] });
  qc.invalidateQueries({ queryKey: ['leads', 'list'] });
  qc.invalidateQueries({ queryKey: ['leads', 'detail'] });
};

export const useCreateQuerySubStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateQuerySubStatusRequest) =>
      querySubStatusesApi.createQuerySubStatus(payload),
    onSuccess: (qss) => {
      invalidateSubStatusFanout(qc);
      toast.success(`Added ${qss.name}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add query sub status'),
  });
};

export const useUpdateQuerySubStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateQuerySubStatusRequest }) =>
      querySubStatusesApi.updateQuerySubStatus(id, payload),
    onSuccess: (qss) => {
      invalidateSubStatusFanout(qc);
      qc.invalidateQueries({ queryKey: QK.detail(qss.id) });
      toast.success('Query sub status updated');
    },
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to update query sub status'),
  });
};

export const useDeleteQuerySubStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => querySubStatusesApi.deleteQuerySubStatus(id),
    onSuccess: () => {
      invalidateSubStatusFanout(qc);
      toast.success('Query sub status deleted');
    },
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to delete query sub status'),
  });
};

export const useBulkDeleteQuerySubStatuses = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => querySubStatusesApi.bulkDeleteQuerySubStatuses(ids),
    onSuccess: () => invalidateSubStatusFanout(qc),
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to delete query sub statuses'),
  });
};

export const useBulkSetQuerySubStatusesStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, isActive }: { ids: string[]; isActive: boolean }) =>
      querySubStatusesApi.bulkSetQuerySubStatusesStatus(ids, isActive),
    onSuccess: () => invalidateSubStatusFanout(qc),
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to update query sub status'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
